import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Pencil, Trash2, ExternalLink, Plus } from "lucide-react";
import { api, img, listingPath, useApi, formatApiError } from "@/lib/api";
import { AdminHeader } from "@/pages/admin/AdminLayout";

export default function AdminListings() {
  const [params] = useSearchParams();
  const kind = params.get("kind") || "sale";
  const { data, reload } = useApi("/admin/listings", []);
  const items = data.filter((l) => l.kind === kind);

  const remove = async (l) => {
    if (!window.confirm(`Delete "${l.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/listings/${l.id}`);
      toast.success("Listing deleted");
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="admin-listings">
      <AdminHeader title={kind === "rent" ? "Places to Stay" : "Homes & Land for Sale"}>
        <Link to={`/admin/listings/new?kind=${kind}`} className="btn-primary" data-testid="admin-listing-new"><Plus className="h-4 w-4" /> New listing</Link>
      </AdminHeader>
      <div className="overflow-hidden rounded-2xl border border-clay bg-white">
        {items.length === 0 && <p className="p-8 text-moss">No listings yet.</p>}
        {items.map((l) => (
          <div key={l.id} className="flex flex-col gap-4 border-b border-clay p-4 last:border-b-0 sm:flex-row sm:items-center" data-testid={`admin-listing-row-${l.slug}`}>
            <img src={img(l.cover_image)} alt="" className="h-20 w-28 shrink-0 rounded-lg object-cover" />
            <div className="min-w-0 flex-1">
              <p className="truncate font-bold text-ink">{l.title}</p>
              <p className="text-sm font-semibold text-forest">{l.kind === "rent" ? l.price_tier : l.price_label}</p>
              <div className="mt-1 flex flex-wrap gap-2 text-xs font-bold">
                <span className={`rounded-full px-2 py-0.5 ${l.published ? "bg-sage/20 text-forest" : "bg-clay text-moss"}`}>{l.published ? "Published" : "Hidden"}</span>
                {l.featured && <span className="rounded-full bg-sand/50 px-2 py-0.5 text-ink">Featured</span>}
                <span className="rounded-full bg-linen px-2 py-0.5 text-moss">{l.images.length} photos</span>
              </div>
            </div>
            <div className="flex gap-2">
              <a href={listingPath(l)} target="_blank" rel="noreferrer" className="rounded-lg border border-clay p-2.5 text-moss hover:text-forest" aria-label="View" data-testid={`admin-listing-view-${l.slug}`}><ExternalLink className="h-4 w-4" /></a>
              <Link to={`/admin/listings/${l.id}`} className="rounded-lg border border-clay p-2.5 text-moss hover:text-forest" aria-label="Edit" data-testid={`admin-listing-edit-${l.slug}`}><Pencil className="h-4 w-4" /></Link>
              <button onClick={() => remove(l)} className="rounded-lg border border-clay p-2.5 text-moss hover:text-destructive" aria-label="Delete" data-testid={`admin-listing-delete-${l.slug}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
